<script lang="ts" setup>
import type { ComponentElementsAccordionItem, ComponentSectionsAccordion, ComponentSectionsMultiAccordion } from '@autobid/strapi-integration/graphql/graphql'
import type { ElementsAccordionItem, SectionsAccordion, SectionsMultiAccordion } from '@autobid/strapi-integration/rest/components'
import { Vue3SlideUpDown } from 'vue3-slide-up-down'

interface Props {
  data:
    | SectionsAccordion
    | SectionsMultiAccordion | ComponentSectionsMultiAccordion | ComponentSectionsAccordion
  nested?: boolean
}

const props = defineProps<Props>()
const activeItems = ref<Record<string | number, boolean>>({})
const initialized = ref(false)
const items = (function () {
  if ('items' in props.data) {
    return props.data.items?.length ? props.data.items : []
  }

  if ('accordions' in props.data) {
    return props.data.accordions?.length ? props.data.accordions : []
  }

  return []
}())

function toggleItem(id: number | string) {
  const result = props.data.alwaysOpen ? { ...activeItems.value } : {}
  const isActive = !!activeItems.value[id]

  result[id] = !isActive
  activeItems.value = result
}

function getTag(item: SectionsAccordion | ElementsAccordionItem | ComponentSectionsAccordion | ComponentElementsAccordionItem) {
  if (props.data && 'elementTag' in props.data && props.data.elementTag) {
    return props.data.elementTag.name
  }

  if ('elementTag' in item && item.elementTag) {
    return item.elementTag.name
  }

  return 'p'
}

onMounted(() => {
  initialized.value = true
})
</script>

<template>
  <template v-for="item in items">
    <article
      v-if="item"
      :key="item.id"
      class="accordion"
      :class="{ 'mb-1.5': nested, 'mb-3 px-3 xl:pl-0': !nested }"
    >
      <header>
        <component :is="getTag(item)">
          <button
            class="flex w-full items-center overflow-hidden px-3 py-2 text-left text-lg font-semibold uppercase"
            :class="{
              'justify-between bg-accordion-primary text-white': !nested,
              'bg-accordion-secondary text-card-primary': nested,
            }"
            @click="toggleItem(item.id || '')"
          >
            <span
              v-if="nested"
              class="relative mr-3 h-6 w-6 min-w-[24px] rounded bg-card-primary"
              role="presentation"
            >
              <!-- plus icon -->
              <span
                role="presentation"
                class="absolute left-1/2 top-1/2 h-1 w-4 -translate-x-1/2 -translate-y-1/2 bg-accordion-secondary"
              />
              <span
                role="presentation"
                class="absolute left-1/2 top-1/2 h-1 w-4 -translate-x-1/2 -translate-y-1/2 rotate-90 bg-accordion-secondary duration-300"
                :class="{
                  'scale-x-0': activeItems[item.id || ''],
                }"
              />
            </span>

            <span>
              {{ item.title ?? '' }}
            </span>

            <Icon
              v-if="!nested"
              mode="svg"
              class="-my-2 h-6 w-6 text-5xl transition-transform duration-300"
              name="fa-solid:chevron-down"
              :class="activeItems[item.id || ''] ? 'rotate-180' : ''"
            />
          </button>
        </component>
      </header>

      <Vue3SlideUpDown
        v-model="activeItems[item.id || '']"
        :duration="250"
      >
        <div v-show="initialized">
          <div v-if="'items' in item" class="mt-3">
            <SectionsAccordion :data="item" :nested="true" />
          </div>

          <main
            v-else-if="'description' in item && item.description"
            class="prose mt-1 max-w-full bg-card-secondary p-3 text-card-primary"
          >
            <SectionsRichTextCkeditor
              :data="{ content: item.description, width: 'w-full' }"
              class="px-0"
            />
          </main>
        </div>
      </Vue3SlideUpDown>
    </article>
  </template>
</template>
